import React, { useEffect, useMemo, useState } from 'react'
import { CampaignType, ICampaignRequest } from '@dataTypes/marketing.type'
import { Button, InputField } from '@components/common'
import { Form } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { ModalTextEditor, MessagePreviewModal } from '@components/layout/Modals'
import { VARIABLE_LIST } from '@constants/Marketing'
import { replaceVariables } from '@utils/marketing.utils'
import { sanitizeEmailHtml } from '@utils/sanitize-html.util'
import { filterCampaignTemplatesBySendChannel } from '@utils/campaign.util'
import {
  attachEmailCanvasToHtml,
  emailCanvasCssVars,
} from '@utils/marketingTemplate.util'
import { fetchAllCampaignTemplates } from '@store/actions/marketing.action'

interface CampaignInfoProps {
  campaignData: ICampaignRequest
  handleChange: (field: string, value: string) => void
  validationErrors: { [key: string]: string }
  isReactivateCampaign: boolean
  googleMailboxConnected?: boolean
  emailSettingsPath?: string
}

const CampaignInfo: React.FC<CampaignInfoProps> = ({
  campaignData,
  handleChange,
  validationErrors,
  isReactivateCampaign,
  googleMailboxConnected = true,
  emailSettingsPath = '/settings/communications/email',
}) => {
  const dispatch = useAppDispatch()
  const clinic = useAppSelector((state) => state.clinic.selectedClinic)
  const campaignTemplates = useAppSelector(
    (state) => state.marketing.campaignTemplates,
  )
  const isEmail = campaignData.channel === 'Email'

  const templatesForSendChannel = useMemo(
    () =>
      filterCampaignTemplatesBySendChannel(
        campaignTemplates,
        campaignData.channel,
      ),
    [campaignTemplates, campaignData.channel],
  )

  const selectedEmailCanvas = useMemo(() => {
    const id = campaignData.campaign_template_id
    if (!id) return undefined
    return campaignTemplates.find((t) => t.campaign_template_id === id)
      ?.email_editor_canvas
  }, [campaignData.campaign_template_id, campaignTemplates])

  const [textInput, setTextInput] = useState<string>(
    campaignData.text_content ?? '',
  )
  const [htmlInput, setHtmlInput] = useState<string>(
    campaignData.html_content ?? '',
  )
  const [description, setDescription] = useState<string>(
    campaignData.description ?? '',
  )
  const [descriptionHtml, setDescriptionHtml] = useState<string>(
    campaignData.description ?? '',
  )
  const [showPreview, setShowPreview] = useState(false)

  useEffect(() => {
    handleChange('text_content', textInput)
  }, [textInput])

  useEffect(() => {
    handleChange(
      'html_content',
      attachEmailCanvasToHtml(htmlInput, selectedEmailCanvas),
    )
  }, [htmlInput, selectedEmailCanvas])

  useEffect(() => {
    handleChange('description', description)
  }, [description])

  useEffect(() => {
    dispatch(fetchAllCampaignTemplates())
  }, [clinic])

  useEffect(() => {
    const id = campaignData.campaign_template_id
    if (!id || !campaignData.channel) return
    const selected = campaignTemplates.find(
      (t) => t.campaign_template_id === id,
    )
    if (selected && selected.channel !== campaignData.channel) {
      handleChange('campaign_template_id', '')
      setTextInput('')
      setHtmlInput('')
    }
  }, [
    campaignData.channel,
    campaignData.campaign_template_id,
    campaignTemplates,
    handleChange,
  ])

  const handleChannelChange = (channel: string): void => {
    if (campaignData.channel === channel) return
    handleChange('channel', channel)
    handleChange('campaign_template_id', '')
    if (channel === 'SMS') {
      handleChange('subject', '')
    }
    setTextInput('')
    setHtmlInput('')
  }

  return (
    <>
      <div className="ac-campaign-info">
        <div className="ac-item">
          <div className="ac-item-input">
            <span className="ac-input-label">Campaign name</span>
            <InputField
              className={
                validationErrors.name ? 'select-field-destructive' : ''
              }
              destructive={!!validationErrors.name}
              hint={validationErrors.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Campaign name"
              value={campaignData.name}
              disabled={isReactivateCampaign}
            />
          </div>
          <div className="ac-item-input">
            <span className="ac-input-label">Campaign type</span>
            <div>
              <Form.Select
                name="type"
                value={campaignData.type ?? ''}
                onChange={(e) => handleChange('type', e.target.value)}
                className={
                  validationErrors.type ? 'select-field-destructive' : ''
                }
                disabled={isReactivateCampaign}
              >
                <option key={'default-null'} value="" disabled />
                {Object.values(CampaignType).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
              {validationErrors.type && (
                <span className="hint-text">{validationErrors.type}</span>
              )}
            </div>
          </div>
        </div>
        <div className="ac-item">
          <div className="ac-item-input">
            <span className="ac-input-label">Select template (Optional)</span>
            <Form.Select
              name="template"
              value={campaignData.campaign_template_id ?? ''}
              onChange={(e) => {
                handleChange('campaign_template_id', e.target.value)
                const template = campaignTemplates.find(
                  (t) => t.campaign_template_id === e.target.value,
                )
                if (template) {
                  setTextInput(template.text_content)
                  setHtmlInput(
                    attachEmailCanvasToHtml(
                      template.html_content,
                      template.email_editor_canvas,
                    ),
                  )
                }
              }}
            >
              <option value=""></option>
              {templatesForSendChannel.map((template) => (
                <option
                  key={template.campaign_template_id}
                  value={template.campaign_template_id}
                >
                  {template.name}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="ac-item-input">
            <span className="ac-input-label">Send via</span>
            <div>
              <div className="ac-item-input-options">
                <Button
                  size="md"
                  className={
                    campaignData.channel === 'SMS'
                      ? 'button-secondary-filled'
                      : 'button-secondary-inactive'
                  }
                  leftIcon={<i className="mgc_message_4_fill" />}
                  onClick={() => handleChannelChange('SMS')}
                  disabled={isReactivateCampaign}
                >
                  SMS
                </Button>
                <Button
                  size="md"
                  className={
                    campaignData.channel === 'Email'
                      ? 'button-secondary-filled'
                      : 'button-secondary-inactive'
                  }
                  leftIcon={<i className="mgc_mail_fill" />}
                  onClick={() => handleChannelChange('Email')}
                  disabled={isReactivateCampaign || !googleMailboxConnected}
                >
                  Email
                </Button>
              </div>
              {!googleMailboxConnected && (
                <span className="hint-text ac-google-mailbox-hint">
                  <span>
                    Connect google mailbox in{' '}
                    <a
                      href={emailSettingsPath}
                      className="ac-email-settings-link"
                    >
                      Email settings
                    </a>
                  </span>
                </span>
              )}
              {validationErrors.channel && (
                <span className="hint-text">{validationErrors.channel}</span>
              )}
            </div>
          </div>
        </div>
        {isEmail && (
          <div className="ac-item">
            <div className="ac-item-input">
              <span className="ac-input-label">Subject</span>
              <InputField
                className={
                  validationErrors.subject ? 'select-field-destructive' : ''
                }
                destructive={!!validationErrors.subject}
                hint={validationErrors.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder="Email subject"
                value={campaignData.subject ?? ''}
                disabled={isReactivateCampaign}
              />
            </div>
          </div>
        )}
        <div className="ac-item">
          <div className="ac-item-input ci-message-input">
            <span className="ac-input-label">Message</span>
            <div
              className={isEmail ? 'mt-marketing-email-tpl-root' : undefined}
              style={
                isEmail ? emailCanvasCssVars(selectedEmailCanvas) : undefined
              }
            >
              <ModalTextEditor
                editorId="campaign-message-editor"
                className={
                  validationErrors.text_content ? 'text-editor-destructive' : ''
                }
                inputText={textInput}
                setInputText={setTextInput}
                inputHtml={htmlInput}
                setInputHtml={setHtmlInput}
                variableList={VARIABLE_LIST}
                showMention={false}
                maxCharCount={isEmail ? undefined : 200}
              />
              {validationErrors.text_content && (
                <span className="hint-text">
                  {validationErrors.text_content}
                </span>
              )}
            </div>
          </div>
          <div className="ac-item-input">
            <span className="ac-input-label">
              Message preview
              <button
                type="button"
                className="unstyled-button mgc_fullscreen_2_line"
                aria-label="Open message preview"
                onClick={() => setShowPreview(true)}
              />
            </span>
            <div
              className={`ac-preview-content ci-message-preview${
                isEmail ? ' ci-message-preview--email' : ''
              }`}
            >
              {isEmail ? (
                <div
                  className="mpc-text mpc-text--email mt-marketing-email-tpl-root"
                  style={emailCanvasCssVars(selectedEmailCanvas)}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeEmailHtml(replaceVariables(htmlInput)),
                  }}
                />
              ) : (
                <div
                  className="mpc-text"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeEmailHtml(replaceVariables(htmlInput)),
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="ac-item">
          <div className="ac-item-input ci-description-input">
            <span className="ac-input-label">Description</span>
            <ModalTextEditor
              editorId="campaign-description-editor"
              inputText={description}
              setInputText={setDescription}
              inputHtml={descriptionHtml}
              setInputHtml={setDescriptionHtml}
              variableList={VARIABLE_LIST}
              showMention={false}
              showLink={false}
              showVariable={false}
              maxCharCount={200}
              readOnly={isReactivateCampaign}
            />
          </div>
        </div>
      </div>
      {showPreview && (
        <>
          <div className="fade modal-backdrop show ci-custom-backdrop" />
          <MessagePreviewModal
            show={showPreview}
            onHide={() => setShowPreview(false)}
            content={replaceVariables(htmlInput)}
            channel={campaignData.channel}
            subject={campaignData.subject}
            emailCanvas={selectedEmailCanvas}
          />
        </>
      )}
    </>
  )
}

export default CampaignInfo
